
<script setup>

import { ref, onMounted, onUnmounted } from 'vue'
import { useI18n } from 'vue-i18n'
import * as Auth from '@/services/Auth'

const { t } = useI18n()

const emailSent = ref(false)
const loading = ref(false)
const error = ref(null)
const verifyParam = ref(null)
const verifyMessage = ref(null)
const showVerifyAlert = ref(false)
let autoHideTimer = null

function closeVerifyAlert() {
  showVerifyAlert.value = false
  if (autoHideTimer) {
    clearTimeout(autoHideTimer)
    autoHideTimer = null
  }
}

async function sendVerificationEmail() {
  loading.value = true
  error.value = null
  try {
    await Auth.sendActivationEmail()
    emailSent.value = true
  } catch (e) {
    error.value = t('alerts.verifyEmailError')
  } finally {
    loading.value = false
  }
}

// Parse query params once on mount to show verification result sent by backend redirect
onMounted(() => {
  try {
    const params = new URLSearchParams(window.location.search)
    // backend may send 'verify'
    const verify = params.get('verify')
    const message = params.get('message')

    if (verify !== null) {
      // normalize common truthy values ('1', 'true')
      verifyParam.value = ['1', 'true', 'yes'].includes(String(verify).toLowerCase())
      verifyMessage.value = message || ''
      showVerifyAlert.value = true
      // clear any existing timer then set a 10s auto-hide timer
      if (autoHideTimer) {
        clearTimeout(autoHideTimer)
        autoHideTimer = null
      }
      autoHideTimer = setTimeout(() => {
        showVerifyAlert.value = false
        clearTimeout(autoHideTimer)
        autoHideTimer = null
      }, 10000)
      // Remove params from URL to avoid showing repeatedly
      try {
        const url = new URL(window.location.href)
        url.searchParams.delete('verify')
        url.searchParams.delete('message')
        window.history.replaceState({}, document.title, url.toString())
      } catch (e) {
        // ignore
      }
    }
  } catch (e) {
    // ignore parsing errors
  }
})

onUnmounted(() => {
  if (autoHideTimer) {
    clearTimeout(autoHideTimer)
    autoHideTimer = null
  }
})
</script>


<template>
  
  <!-- Alert shown after backend redirects with verify/message query params -->
  <v-fade-transition>
    <v-alert
      v-if="showVerifyAlert"
      :type="verifyParam ? 'success' : 'error'"
      :color="verifyParam ? 'success' : 'error'"
      closable
      class="mx-4 my-2"
      variant="outlined"
      colored-border
      elevation="2"
      @click:close="closeVerifyAlert()"
    >
      {{ verifyMessage || (verifyParam ? t('alerts.verifyEmailSuccess') : t('alerts.verifyEmailError')) }}
    </v-alert>
  </v-fade-transition>
  
  <div v-if="Auth.isAuthenticated() && !Auth.isVerified()" class="my-6">
    <v-alert
      v-if="!emailSent"
      closable
      color="amber"
      class="mx-4 my-2"
      variant="outlined"
      colored-border
      elevation="2"
    >
      {{ t('alerts.verifyEmail') }}
      <div class="mt-4">
        <v-btn
          color="warning"
          variant="tonal"
          :loading="loading"
          :disabled="loading"
          @click="sendVerificationEmail"
        >
          {{ t('actions.verifyEmail') }}
        </v-btn>
      </div>
      <div v-if="error" class="mt-2 text-error">{{ error }}</div>
    </v-alert>
    <v-alert
      v-else
      closable
      type="info"
      color="info"
      class="mx-4 my-2"
      variant="outlined"
      colored-border
      elevation="2"
    >
      {{ t('alerts.verifyEmailSent') }}
    </v-alert>
  </div>
</template>